import React, { useState, useEffect } from 'react';
import { FiEye, FiTrash2, FiPackage, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Loading from '../components/Loading';
import './OrdenesCompraPage.css';

const GuiasDespachoPage = () => {
  const [guias, setGuias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    numero_guia: '',
    orden_compra: '',
    proveedor: '',
    proyecto: '',
    estado: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  const [showFiltros, setShowFiltros] = useState(false);

  useEffect(() => {
    cargarGuias();
  }, []);

  const cargarGuias = async () => {
    try {
      setLoading(true);
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/bodega');

      // Construir query params desde filtros
      const params = new URLSearchParams();
      if (filtros.numero_guia) params.append('numero_guia__icontains', filtros.numero_guia);
      if (filtros.orden_compra) params.append('orden_compra__numero_oc__icontains', filtros.orden_compra);
      if (filtros.proveedor) params.append('orden_compra__proveedor__nombre__icontains', filtros.proveedor);
      if (filtros.proyecto) params.append('orden_compra__proyecto__nombre__icontains', filtros.proyecto);
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.fecha_desde) params.append('fecha_recepcion__gte', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_recepcion__lte', filtros.fecha_hasta);

      const queryString = params.toString();
      const url = `${API_BASE_URL}/guias-despacho/${queryString ? '?' + queryString : ''}`;

      const response = await api.get(url);
      const guiasData = response.data.results || response.data;
      setGuias(Array.isArray(guiasData) ? guiasData : []);
    } catch (error) {
      console.error('Error al cargar guías de despacho:', error);
      toast.error('Error al cargar las guías de despacho');
      setGuias([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const aplicarFiltros = () => {
    cargarGuias();
  };

  const limpiarFiltros = () => {
    setFiltros({
      numero_guia: '',
      orden_compra: '',
      proveedor: '',
      proyecto: '',
      estado: '',
      fecha_desde: '',
      fecha_hasta: ''
    });
    // Recargar sin filtros
    setTimeout(() => cargarGuias(), 100);
  };

  const handleEliminarGuia = async (guia) => {
    if (!window.confirm(`¿Está seguro de eliminar la guía ${guia.numero_guia}?`)) {
      return;
    }

    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/bodega');
      await api.delete(`${API_BASE_URL}/guias-despacho/${guia.id}/`);

      toast.success('Guía de despacho eliminada correctamente');
      cargarGuias();
    } catch (error) {
      console.error('Error al eliminar guía:', error);
      toast.error('Error al eliminar la guía de despacho');
    }
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado) {
      case 'borrador':
        return 'badge-warning';
      case 'recepcionado':
        return 'badge-success';
      default:
        return 'badge-secondary';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'borrador':
        return 'Borrador';
      case 'recepcionado':
        return 'Recepcionado';
      default:
        return estado;
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const calcularTotalGuia = (guia) => {
    if (!guia.detalles || guia.detalles.length === 0) return 0;
    return guia.detalles.reduce((sum, detalle) => {
      return sum + (parseFloat(detalle.subtotal) || 0);
    }, 0);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <FiPackage size={32} className="page-icon" />
          <div>
            <h1>Guías de Despacho</h1>
            <p className="page-subtitle">Gestión de recepciones de compra</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-filtro"
            onClick={() => setShowFiltros(!showFiltros)}
          >
            <FiFilter size={18} />
            {showFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
        </div>
      </div>

      {/* Panel de filtros */}
      {showFiltros && (
        <div className="filtros-panel">
          <div className="filtros-grid">
            <div className="form-group">
              <label>Número de Guía</label>
              <input
                type="text"
                name="numero_guia"
                value={filtros.numero_guia}
                onChange={handleFiltroChange}
                placeholder="Buscar por número..."
              />
            </div>
            <div className="form-group">
              <label>Orden de Compra</label>
              <input
                type="text"
                name="orden_compra"
                value={filtros.orden_compra}
                onChange={handleFiltroChange}
                placeholder="Buscar por OC..."
              />
            </div>
            <div className="form-group">
              <label>Proveedor</label>
              <input
                type="text"
                name="proveedor"
                value={filtros.proveedor}
                onChange={handleFiltroChange}
                placeholder="Buscar por proveedor..."
              />
            </div>
            <div className="form-group">
              <label>Proyecto</label>
              <input
                type="text"
                name="proyecto"
                value={filtros.proyecto}
                onChange={handleFiltroChange}
                placeholder="Buscar por proyecto..."
              />
            </div>
            <div className="form-group">
              <label>Estado</label>
              <select
                name="estado"
                value={filtros.estado}
                onChange={handleFiltroChange}
              >
                <option value="">Todos</option>
                <option value="borrador">Borrador</option>
                <option value="recepcionado">Recepcionado</option>
              </select>
            </div>
            <div className="form-group">
              <label>Fecha Desde</label>
              <input
                type="date"
                name="fecha_desde"
                value={filtros.fecha_desde}
                onChange={handleFiltroChange}
              />
            </div>
            <div className="form-group">
              <label>Fecha Hasta</label>
              <input
                type="date"
                name="fecha_hasta"
                value={filtros.fecha_hasta}
                onChange={handleFiltroChange}
              />
            </div>
          </div>
          <div className="filtros-actions">
            <button className="btn-secondary" onClick={limpiarFiltros}>
              Limpiar
            </button>
            <button className="btn-primary" onClick={aplicarFiltros}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Tabla de guías */}
      <div className="table-container">
        {guias.length === 0 ? (
          <div className="empty-state">
            <FiPackage size={64} />
            <h3>No hay guías de despacho</h3>
            <p>Las guías se crean al recepcionar órdenes de compra</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>N° Guía</th>
                <th>Orden de Compra</th>
                <th>Proveedor</th>
                <th>Proyecto</th>
                <th>Bodega</th>
                <th>Fecha Recepción</th>
                <th>Fecha Emisión Guía</th>
                <th>Estado</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {guias.map(guia => (
                <tr key={guia.id}>
                  <td>
                    <strong>{guia.numero_guia}</strong>
                  </td>
                  <td>{guia.orden_compra_numero || '-'}</td>
                  <td>{guia.proveedor_nombre || '-'}</td>
                  <td>{guia.proyecto_nombre || '-'}</td>
                  <td>{guia.bodega_nombre || '-'}</td>
                  <td>{formatFecha(guia.fecha_recepcion)}</td>
                  <td>{formatFecha(guia.fecha_emision_guia)}</td>
                  <td>
                    <span className={`badge ${getEstadoBadgeClass(guia.estado)}`}>
                      {getEstadoLabel(guia.estado)}
                    </span>
                  </td>
                  <td>
                    <strong>{formatCurrency(calcularTotalGuia(guia))}</strong>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-action"
                        onClick={() => {
                          // TODO: Implementar modal de visualización
                          toast.info('Funcionalidad en desarrollo');
                        }}
                        title="Ver Detalle"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#007bff',
                          padding: '4px'
                        }}
                      >
                        <FiEye size={18} />
                      </button>
                      <button
                        className="btn-icon-action danger"
                        onClick={() => handleEliminarGuia(guia)}
                        title="Eliminar"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#dc3545',
                          padding: '4px'
                        }}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Resumen */}
      {guias.length > 0 && (
        <div className="page-footer">
          <div className="summary-stats">
            <div className="stat-card">
              <span className="stat-label">Total Guías</span>
              <span className="stat-value">{guias.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Recepcionadas</span>
              <span className="stat-value">
                {guias.filter(g => g.estado === 'recepcionado').length}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Borradores</span>
              <span className="stat-value">
                {guias.filter(g => g.estado === 'borrador').length}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Recepcionado</span>
              <span className="stat-value">
                {formatCurrency(
                  guias
                    .filter(g => g.estado === 'recepcionado')
                    .reduce((sum, g) => sum + calcularTotalGuia(g), 0)
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuiasDespachoPage;
